import React, { useState, useMemo } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import Autocomplete from '@mui/material/Autocomplete';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CircularProgress from '@mui/material/CircularProgress';
import FormHelperText from '@mui/material/FormHelperText';
import { Button, TextField, Select } from './ui-kit';
import { getPermissionChipSx } from '../utils/serviceColors';

function formatPermission(p) {
  if (p?.service && p?.resource && p?.action) {
    return `${p.service}.${p.resource}.${p.action}`;
  }
  return p?.name || 'Unknown permission';
}

function isWildcardPermission(p) {
  return !!(p?.isWildcard || p?.permission?.isWildcard);
}

function conditionsCount(p) {
  return p?.conditions ? Object.keys(p.conditions).length : 0;
}

function PermissionsPicker({
  availablePermissions = [],
  value = [],
  onChange,
  loading = false,
  disabled = false,
  error = false,
  helperText = '',
  label = 'Search permissions',
  onEditConditions,
  renderActions,
}) {
  const [selectedService, setSelectedService] = useState('');
  const [selectedResource, setSelectedResource] = useState('');
  const [selectedAction, setSelectedAction] = useState('');
  const [search, setSearch] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewItems, setPreviewItems] = useState([]);
  const [previewChecked, setPreviewChecked] = useState(new Set());

  const services = useMemo(
    () => [...new Set(availablePermissions.map((p) => p.service).filter(Boolean))].sort(),
    [availablePermissions]
  );

  const resources = useMemo(
    () =>
      [
        ...new Set(
          availablePermissions
            .filter((p) => !selectedService || p.service === selectedService)
            .map((p) => p.resource)
            .filter(Boolean)
        ),
      ].sort(),
    [availablePermissions, selectedService]
  );

  const actions = useMemo(
    () =>
      [
        ...new Set(
          availablePermissions
            .filter(
              (p) =>
                (!selectedService || p.service === selectedService) &&
                (!selectedResource || p.resource === selectedResource)
            )
            .map((p) => p.action)
            .filter(Boolean)
        ),
      ].sort(),
    [availablePermissions, selectedService, selectedResource]
  );

  const filtered = useMemo(
    () =>
      availablePermissions.filter(
        (p) =>
          (!selectedService || p.service === selectedService) &&
          (!selectedResource || p.resource === selectedResource) &&
          (!selectedAction || p.action === selectedAction)
      ),
    [availablePermissions, selectedService, selectedResource, selectedAction]
  );

  const selectedIds = useMemo(() => new Set(value.map((p) => p._id)), [value]);
  const toAddFromFilter = useMemo(
    () => filtered.filter((p) => !selectedIds.has(p._id)),
    [filtered, selectedIds]
  );

  const hasAnyFilter = !!(selectedService || selectedResource || selectedAction);

  const sortedSelected = useMemo(
    () =>
      [...value].sort((a, b) => {
        const sa = a.service || '';
        const sb = b.service || '';
        if (sa !== sb) return sa.localeCompare(sb);
        if ((a.resource || '') !== (b.resource || '')) {
          return (a.resource || '').localeCompare(b.resource || '');
        }
        return (a.action || '').localeCompare(b.action || '');
      }),
    [value]
  );

  const openPreview = (items) => {
    setPreviewItems(items);
    setPreviewChecked(new Set(items.map((p) => p._id)));
    setPreviewOpen(true);
  };

  const confirmPreview = () => {
    const toAdd = previewItems.filter((p) => previewChecked.has(p._id));
    if (toAdd.length === 0) {
      setPreviewOpen(false);
      return;
    }
    const merged = [...value];
    const existing = new Set(merged.map((p) => p._id));
    toAdd.forEach((p) => {
      if (!existing.has(p._id)) merged.push(p);
    });
    onChange(merged);
    setPreviewOpen(false);
  };

  const removeOne = (permission) => {
    onChange(value.filter((p) => p._id !== permission._id));
  };

  const clearFilters = () => {
    setSelectedService('');
    setSelectedResource('');
    setSelectedAction('');
  };

  return (
    <Box>
      {/* Filter row */}
      {availablePermissions.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Grid container spacing={1.5} alignItems="flex-end">
            <Grid item xs={12} sm={3}>
              <Select
                value={selectedService}
                onChange={(e) => {
                  setSelectedService(e.target.value);
                  setSelectedResource('');
                  setSelectedAction('');
                }}
                label="Service"
                fullWidth
                disabled={disabled}
              >
                <MenuItem value=""><em>All services</em></MenuItem>
                {services.map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Select
                value={selectedResource}
                onChange={(e) => {
                  setSelectedResource(e.target.value);
                  setSelectedAction('');
                }}
                label="Resource"
                fullWidth
                disabled={disabled}
              >
                <MenuItem value=""><em>All resources</em></MenuItem>
                {resources.map((r) => (
                  <MenuItem key={r} value={r}>{r}</MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Select
                value={selectedAction}
                onChange={(e) => setSelectedAction(e.target.value)}
                label="Action"
                fullWidth
                disabled={disabled}
              >
                <MenuItem value=""><em>All actions</em></MenuItem>
                {actions.map((a) => (
                  <MenuItem key={a} value={a}>{a}</MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button
                variant="outlined"
                fullWidth
                sx={{ height: '48px' }}
                disabled={disabled || !hasAnyFilter}
                onClick={clearFilters}
              >
                Clear filters
              </Button>
            </Grid>
          </Grid>

          {hasAnyFilter && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {filtered.length} match filter
                {toAddFromFilter.length > 0
                  ? `, ${toAddFromFilter.length} not yet added`
                  : ' (all already added)'}
              </Typography>
              {toAddFromFilter.length > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  disabled={disabled}
                  onClick={() => openPreview(toAddFromFilter)}
                >
                  Add all {toAddFromFilter.length} filtered
                </Button>
              )}
            </Box>
          )}
        </Box>
      )}

      <Autocomplete
        multiple
        disableCloseOnSelect
        disabled={disabled}
        options={filtered}
        value={value}
        inputValue={search}
        onInputChange={(_, v, reason) => {
          if (reason !== 'reset') setSearch(v);
        }}
        onChange={(_, newValue, reason) => {
          if (reason === 'clear') {
            setSearch('');
            return;
          }
          onChange(newValue);
        }}
        isOptionEqualToValue={(option, val) => option._id === val._id}
        renderTags={() => null}
        getOptionLabel={formatPermission}
        groupBy={(option) => option.service || ''}
        loading={loading}
        noOptionsText="No permissions found"
        loadingText="Loading permissions..."
        ListboxProps={{ style: { maxHeight: 320 } }}
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="body2" fontFamily="monospace" fontWeight={500}>
                {formatPermission(option)}
              </Typography>
              {option.description && (
                <Typography variant="caption" color="text.secondary">
                  {option.description}
                </Typography>
              )}
            </Box>
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder="Type to search or pick from filters above..."
            error={error}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {error && helperText && (
        <FormHelperText error sx={{ ml: 1.5 }}>{helperText}</FormHelperText>
      )}

      {/* Selected chips */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Current permissions ({value.length})
        </Typography>

        {value.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No permissions selected yet. Use the filters above or search to add some.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {sortedSelected.map((p) => {
              const wildcard = isWildcardPermission(p);
              const condCount = onEditConditions ? conditionsCount(p) : 0;
              const tooltip = (
                <Box sx={{ fontSize: '0.75rem' }}>
                  <div><strong>{formatPermission(p)}</strong></div>
                  {p.description && <div style={{ color: '#ccc' }}>{p.description}</div>}
                  {wildcard && <div>Wildcard permission</div>}
                  {condCount > 0 && <div>Conditions: {condCount}</div>}
                  {onEditConditions && !disabled && (
                    <div style={{ marginTop: 4, color: '#aaa', fontSize: '0.7rem' }}>
                      Click to edit conditions
                    </div>
                  )}
                </Box>
              );
              const chip = (
                <Chip
                  size="small"
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <span>{formatPermission(p)}</span>
                      {condCount > 0 && (
                        <Chip
                          label="C"
                          size="small"
                          sx={{
                            height: 14,
                            fontSize: '0.6rem',
                            fontWeight: 700,
                            bgcolor: 'rgba(255,255,255,0.25)',
                            color: '#fff',
                            '& .MuiChip-label': { px: 0.5 },
                          }}
                        />
                      )}
                      {wildcard && (
                        <Chip
                          label="*"
                          size="small"
                          sx={{
                            height: 14,
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            bgcolor: 'rgba(255,255,255,0.25)',
                            color: '#fff',
                            '& .MuiChip-label': { px: 0.5 },
                          }}
                        />
                      )}
                    </Box>
                  }
                  onClick={onEditConditions && !disabled ? () => onEditConditions(p) : undefined}
                  onDelete={disabled ? undefined : () => removeOne(p)}
                  disabled={disabled}
                  sx={{
                    cursor: onEditConditions && !disabled ? 'pointer' : 'default',
                    ...getPermissionChipSx(p),
                  }}
                />
              );
              return (
                <Tooltip key={p._id} title={tooltip} arrow disableInteractive>
                  {chip}
                </Tooltip>
              );
            })}
          </Box>
        )}

        {renderActions && !disabled && (
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            {renderActions()}
          </Box>
        )}
      </Box>

      {/* Preview dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Add permissions</span>
            <Typography variant="caption" color="text.secondary">
              {previewChecked.size} of {previewItems.length} selected
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 2,
              py: 1,
              borderBottom: '2px solid',
              borderColor: 'divider',
              bgcolor: 'action.hover',
              cursor: 'pointer',
            }}
            onClick={() => {
              if (previewChecked.size === previewItems.length) {
                setPreviewChecked(new Set());
              } else {
                setPreviewChecked(new Set(previewItems.map((p) => p._id)));
              }
            }}
          >
            <Checkbox
              checked={previewChecked.size === previewItems.length && previewItems.length > 0}
              indeterminate={
                previewChecked.size > 0 && previewChecked.size < previewItems.length
              }
              onChange={() => {}}
              size="small"
            />
            <Typography variant="body2" fontWeight={600} sx={{ ml: 0.5 }}>
              Select all
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              ({previewItems.length} permissions)
            </Typography>
          </Box>

          <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
            {previewItems.map((p) => {
              const checked = previewChecked.has(p._id);
              return (
                <Box
                  key={p._id}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    px: 2,
                    py: 0.75,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    cursor: 'pointer',
                    bgcolor: checked ? 'action.selected' : 'transparent',
                    '&:hover': { bgcolor: checked ? 'action.selected' : 'action.hover' },
                  }}
                  onClick={() => {
                    const next = new Set(previewChecked);
                    if (next.has(p._id)) next.delete(p._id);
                    else next.add(p._id);
                    setPreviewChecked(next);
                  }}
                >
                  <Checkbox
                    checked={checked}
                    onChange={() => {}}
                    size="small"
                    sx={{ mt: p.description ? 0 : 0.25, flexShrink: 0 }}
                  />
                  <Box sx={{ ml: 0.5, py: 0.25 }}>
                    <Typography variant="body2" fontFamily="monospace" fontWeight={checked ? 600 : 400}>
                      {formatPermission(p)}
                    </Typography>
                    {p.description && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        {p.description}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setPreviewOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button variant="contained" disabled={previewChecked.size === 0} onClick={confirmPreview}>
            Add {previewChecked.size > 0 ? previewChecked.size : ''} Permission
            {previewChecked.size !== 1 ? 's' : ''}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}

export default PermissionsPicker;
